/**
 * guardianEncounterContent — per-Guardian encounter phase authored content.
 *
 * Round 18 Phase E (AGE-1252): 4 Guardians × 4 tonal phases = 16 content sets.
 * Source of truth: docs/design/guardian-encounter-phases.md §1–§5.
 *
 * Each Guardian has four tonal beats (Stillness, Weariness, Remembrance, Rest)
 * keyed to remaining HP percentage. This module defines:
 *   - Behavior tuning per phase (swing timing, movement style)
 *   - Ambient/environmental descriptions (for client rendering)
 *   - Resonance vision text (for journal Whisper grants)
 *   - Camp mood descriptions (fire, pet, food effects)
 *   - Witness conditions (how many players, what triggers progression)
 *   - Non-combat contribution configs per Guardian
 *   - Unburdening text (final journal entry)
 *
 * @packageDocumentation
 */
// ── Enums & types ──
/** Tonal encounter phases, keyed to HP%. Universal across all Guardians. */
export var GuardianTonalPhase;
(function (GuardianTonalPhase) {
    /** 100–75% HP: habitual, metronomic, heavy but not hostile. */
    GuardianTonalPhase["Stillness"] = "stillness";
    /** 75–50% HP: pattern fraying, slower, tired. */
    GuardianTonalPhase["Weariness"] = "weariness";
    /** 50–25% HP: remembers who they were, emotional peak. */
    GuardianTonalPhase["Remembrance"] = "remembrance";
    /** 25–0% HP: waiting for permission to stop. */
    GuardianTonalPhase["Rest"] = "rest";
})(GuardianTonalPhase || (GuardianTonalPhase = {}));
/** HP percentage boundaries for each tonal phase. */
export const TONAL_PHASE_HP_RANGES = {
    [GuardianTonalPhase.Stillness]: { min: 75, max: 100 },
    [GuardianTonalPhase.Weariness]: { min: 50, max: 75 },
    [GuardianTonalPhase.Remembrance]: { min: 25, max: 50 },
    [GuardianTonalPhase.Rest]: { min: 0, max: 25 },
};
// ── Universal resonance Whispers (used across all Guardians) ──
export const UNIVERSAL_RESONANCE_WHISPERS = [
    'They almost remembered.',
    'Something passed through the room that was not the wind.',
    'The air tasted the way it used to.',
    'For a moment, the world was not Softened.',
    'They looked at you. Not through you. At you.',
    'The silence afterward was different from the silence before.',
];
// ── Per-Guardian content ──────────────────────────────────────────────────
// §3.1 — Hollow Warden (Verdana)
const HOLLOW_WARDEN_CONTENT = {
    guardianType: 'hollow_warden',
    name: 'Hollow Warden',
    realmKey: 'verdana_fields',
    baseHp: 800_000,
    durationTargetDays: 7,
    finalStillnessThreshold: 10,
    unburdeningRitualDurationMinutes: 5,
    unburdeningRitualDurationSoloMinutes: 10,
    unburdeningText: 'The vines climbed. They had been waiting for permission.',
    relicKey: 'wardens_vine',
    contributions: [
        {
            key: 'vine_offering',
            name: 'Vine Offering',
            description: 'Place vine cuttings from Verdana healed zones at camp. Removes the 10% damage penalty for 8–12 hours.',
            hpEquivalentPerUse: 0,
            ritualCategory: 'labor',
            durationMinutes: 5,
            materials: { vine_cutting: 1 },
        },
        {
            key: 'sapling_tending',
            name: 'Sapling Tending',
            description: 'Tend saplings near the Warden\'s chamber. Each sapling contributes ~10 HP equivalent and feeds realm restoration.',
            hpEquivalentPerUse: 10,
            ritualCategory: 'restoration',
            durationMinutes: 10,
            materials: { sapling: 1 },
        },
    ],
    phases: {
        [GuardianTonalPhase.Stillness]: {
            phase: GuardianTonalPhase.Stillness,
            behavior: {
                swingWindupMultiplier: 1.0,
                skipsSwings: false,
                movementStyle: 'rooted',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'steady_bright',
                petBehavior: 'Curious — sniffs the Guardian, returns to camp.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Vines cover walls and floor. Thick. Still. No flowers.',
            resonanceVision: 'Vines briefly flower — white and yellow blossoms open along the walls, then close. The Warden reaches toward a blossom and stops.',
            whisperPool: [
                'We arrived. It is large. It is tired. We are small. We will be patient.',
                'The vines have been still for so long they have forgotten how to grow.',
                'There is something here that used to be green.',
            ],
        },
        [GuardianTonalPhase.Weariness]: {
            phase: GuardianTonalPhase.Weariness,
            behavior: {
                swingWindupMultiplier: 1.15,
                skipsSwings: false,
                movementStyle: 'drift',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'flicker_on_drift',
                petBehavior: 'Comfortable — sleeps near fire, fetches small materials.',
                foodBuffMultiplier: 1.1,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Vines begin to thin. Patches of bare stone show through.',
            resonanceVision: 'Vines climb — for 60 seconds, they grow upward as they once did. The Warden watches. A vine touches the ceiling and curls.',
            whisperPool: [
                'The days run together. That is how you know it is working.',
                'They paused, briefly, as if they had forgotten the next step of the patrol.',
                'Something loosened in the room. Not much. Enough to notice.',
            ],
        },
        [GuardianTonalPhase.Remembrance]: {
            phase: GuardianTonalPhase.Remembrance,
            behavior: {
                swingWindupMultiplier: 1.25,
                skipsSwings: true,
                movementStyle: 'wander',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'bright_during_resonance',
                petBehavior: 'Attentive — faces Guardian during resonance, chirps or hums.',
                foodBuffMultiplier: 1.1,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 2,
                durationSeconds: 75,
            },
            environmentalShift: 'Bare stone dominates. Remaining vines are thin and pale. Sunlight-colored light enters from above.',
            resonanceVision: 'The room becomes a garden. The Warden kneels. Vine-allies arrange themselves in a circle. For 60 seconds, everything is green and growing.',
            whisperPool: [
                'They are beginning to remember. I think we are helping.',
                'For a moment, the vines climbed. For a moment, the wind blew.',
                'The garden was always here. It was just waiting underneath.',
            ],
        },
        [GuardianTonalPhase.Rest]: {
            phase: GuardianTonalPhase.Rest,
            behavior: {
                swingWindupMultiplier: 1.4,
                skipsSwings: true,
                movementStyle: 'stationary',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'low_ember_warm',
                petBehavior: 'Asleep against the player. Occasionally opens one eye toward the Guardian.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1, // continuous low-level — handled by client VFX
                durationSeconds: 90,
            },
            environmentalShift: 'Bare stone. A single vine, green and alive, runs along the floor to the Warden.',
            resonanceVision: 'Petals drift. No wind. They fall because it is time.',
            whisperPool: [
                'Almost. Be gentle. They are almost done.',
                'The quiet held them. It was enough.',
                'Home. Rest. Enough.',
            ],
        },
    },
};
// §3.2 — Duneshade (Sablewind)
const DUNESHADE_CONTENT = {
    guardianType: 'duneshade',
    name: 'Duneshade',
    realmKey: 'sablewind_reaches',
    baseHp: 1_800_000,
    durationTargetDays: 14,
    finalStillnessThreshold: 10,
    unburdeningRitualDurationMinutes: 5,
    unburdeningRitualDurationSoloMinutes: 10,
    unburdeningText: 'The wind remembered. It stood up and blew, and the shadow was cast by something real.',
    relicKey: 'duneshades_kite_string',
    contributions: [
        {
            key: 'kite_vigil',
            name: 'Kite Vigil',
            description: 'Fly a kite near Duneshade to make them visible. Each kite-minute contributes ~0.5 HP through visibility pressure.',
            hpEquivalentPerUse: 15, // ~0.5 HP/min × 30 min kite duration
            ritualCategory: 'vigil',
            durationMinutes: 5, // setup time
            materials: { cloth: 1, string: 1 },
        },
        {
            key: 'sing_to_wind',
            name: 'Sing to the Wind',
            description: 'Sing during a resonance event. Contributes ~30 HP and extends the resonance by 10 seconds.',
            hpEquivalentPerUse: 30,
            ritualCategory: 'vigil',
            durationMinutes: 3,
            materials: {},
        },
    ],
    phases: {
        [GuardianTonalPhase.Stillness]: {
            phase: GuardianTonalPhase.Stillness,
            behavior: {
                swingWindupMultiplier: 1.0,
                skipsSwings: false,
                movementStyle: 'rooted',
                alwaysVisible: false, // visible only when kite is aloft
            },
            campMood: {
                fireState: 'steady_bright',
                petBehavior: 'Nervous — huddles near player, ears flat.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Sand is still. No wind. Dunes are frozen mid-drift.',
            resonanceVision: 'A wind blows through the chamber. Sand lifts and swirls in patterns — spirals, letters in no language. Duneshade solidifies fully for the first time. They face the wind.',
            whisperPool: [
                'We arrived. The sand does not move. Nothing does.',
                'There is a shape in the dark. You can only see it when the kite is up.',
                'The shadow has weight. You feel it before you see it.',
            ],
        },
        [GuardianTonalPhase.Weariness]: {
            phase: GuardianTonalPhase.Weariness,
            behavior: {
                swingWindupMultiplier: 1.15,
                skipsSwings: false,
                movementStyle: 'drift',
                alwaysVisible: false, // lingers visible 5s longer per cycle
            },
            campMood: {
                fireState: 'flicker_on_drift',
                petBehavior: 'Alert — watches the sand for movement, ears up.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Faint breeze. Sand shifts, barely. Grains catch light.',
            resonanceVision: 'The wind sings. A melody — not words, just tones. Duneshade sways. Players who Sing-to-the-Wind hear the melody echo in their own voice.',
            whisperPool: [
                'The days run together. That is how you know it is working.',
                'The sand moved. Just a grain. But it moved.',
                'They lingered a moment longer. Long enough to be seen.',
            ],
        },
        [GuardianTonalPhase.Remembrance]: {
            phase: GuardianTonalPhase.Remembrance,
            behavior: {
                swingWindupMultiplier: 1.25,
                skipsSwings: true,
                movementStyle: 'wander',
                alwaysVisible: true, // visible 50% of the time without kites
            },
            campMood: {
                fireState: 'bright_during_resonance',
                petBehavior: 'Curious — sniffs toward Duneshade during visibility windows.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 2,
                durationSeconds: 75,
            },
            environmentalShift: 'Sustained breeze. Sand dunes shift slowly. Kite-strings hum.',
            resonanceVision: 'The chamber becomes an open desert at dusk. The wind is strong. Duneshade stands at full height — they were tall, once. They hold a kite. For 60 seconds, the shadow has a shape.',
            whisperPool: [
                'They are beginning to remember. I think we are helping.',
                'The shadow has a shape now. It looks like a person.',
                'They flew the kite themselves. Just for a moment.',
            ],
        },
        [GuardianTonalPhase.Rest]: {
            phase: GuardianTonalPhase.Rest,
            behavior: {
                swingWindupMultiplier: 1.4,
                skipsSwings: true,
                movementStyle: 'stationary',
                alwaysVisible: true, // translucent, not hidden
            },
            campMood: {
                fireState: 'low_ember_warm',
                petBehavior: 'Calm — sits facing Duneshade, no longer afraid.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 90,
            },
            environmentalShift: 'Still. The sand has settled into smooth planes. Moonlight (or its memory) enters the chamber.',
            resonanceVision: 'Duneshade\'s shadow stretches behind them — a person\'s shadow. Not a monster\'s.',
            whisperPool: [
                'Almost. Be gentle. They are almost done.',
                'The wind remembered how to blow. The shadow remembered how to fall.',
                'They stood still. That is all they needed to do.',
            ],
        },
    },
};
// §3.3 — Frostbound Matriarch (Frostmere)
const FROSTBOUND_MATRIARCH_CONTENT = {
    guardianType: 'frostbound_matriarch',
    name: 'Frostbound Matriarch',
    realmKey: 'frostmere_expanse',
    baseHp: 2_200_000,
    durationTargetDays: 14,
    finalStillnessThreshold: 10,
    unburdeningRitualDurationMinutes: 5,
    unburdeningRitualDurationSoloMinutes: 10,
    unburdeningText: 'She sat by the fire. She had not been warm in a very long time. She closed her eyes.',
    relicKey: 'matriarchs_chill_ember',
    contributions: [
        {
            key: 'hearth_tending',
            name: 'Hearth Tending',
            description: 'Reinforce the camp hearth. Reduces the Matriarch\'s Longing debuff duration by 50% for 4 real hours.',
            hpEquivalentPerUse: 0,
            ritualCategory: 'labor',
            durationMinutes: 10,
            materials: { stone: 2, wood: 1 },
        },
        {
            key: 'warm_cooking',
            name: 'Warm Cooking',
            description: 'Prepare warm food at the hearth during a resonance. Cleanses Longing from nearby players and contributes ~100 HP equivalent.',
            hpEquivalentPerUse: 100,
            ritualCategory: 'labor',
            durationMinutes: 8,
            materials: { soup_ingredient: 1 },
        },
    ],
    phases: {
        [GuardianTonalPhase.Stillness]: {
            phase: GuardianTonalPhase.Stillness,
            behavior: {
                swingWindupMultiplier: 1.0,
                skipsSwings: false,
                movementStyle: 'rooted', // teleports constantly but anchor is fixed
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'steady_bright',
                petBehavior: 'Shivering — pressed close to the fire.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Chamber is deep ice. Walls reflect. Cold light. Everything is sharp and still.',
            resonanceVision: 'The Matriarch stands still for the first time. She looks at her hands. They are translucent — you can see warm light inside the ice. Then she teleports away.',
            whisperPool: [
                'We arrived. It is cold. She does not stay in one place.',
                'The ice is old. Older than the Softening, maybe.',
                'She moved so fast. But for a moment, she stopped.',
            ],
        },
        [GuardianTonalPhase.Weariness]: {
            phase: GuardianTonalPhase.Weariness,
            behavior: {
                swingWindupMultiplier: 1.15,
                skipsSwings: false,
                movementStyle: 'drift', // teleport interval lengthened
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'flicker_on_drift',
                petBehavior: 'Warming — moves between fire and player, less shivering.',
                foodBuffMultiplier: 1.1,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 90,
            },
            environmentalShift: 'Ice thins at edges. Water droplets form on walls. Barely visible.',
            resonanceVision: 'She stands by the camp hearth. She holds her hands toward the fire. She does not teleport for 90 seconds. Her ice dims.',
            whisperPool: [
                'The days run together. That is how you know it is working.',
                'A drop of water. On the wall. The first one.',
                'She stood by the fire. She did not attack. She just stood.',
            ],
        },
        [GuardianTonalPhase.Remembrance]: {
            phase: GuardianTonalPhase.Remembrance,
            behavior: {
                swingWindupMultiplier: 1.25,
                skipsSwings: true,
                movementStyle: 'approach_hearth', // walks between positions
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'bright_during_resonance',
                petBehavior: 'Attentive — faces the Matriarch without fear during resonance.',
                foodBuffMultiplier: 1.15,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 2,
                durationSeconds: 75,
            },
            environmentalShift: 'Water runs. Thin streams along walls. Ice retreats from the center. The room smells like spring.',
            resonanceVision: 'The ice melts from one wall, revealing stone beneath — and on the stone, a painting. A family. The Matriarch touches it. She was one of them.',
            whisperPool: [
                'They are beginning to remember. I think we are helping.',
                'She walked. She did not teleport. She walked.',
                'There was a painting. Under the ice. A family.',
            ],
        },
        [GuardianTonalPhase.Rest]: {
            phase: GuardianTonalPhase.Rest,
            behavior: {
                swingWindupMultiplier: 1.4,
                skipsSwings: true,
                movementStyle: 'approach_hearth', // walks slowly toward warmth
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'low_ember_warm',
                petBehavior: 'Asleep — curled up, no longer shivering. Warm.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 90,
            },
            environmentalShift: 'Water everywhere. Shallow pools. The cold is gone. The chamber is a cave with a warm floor.',
            resonanceVision: 'Her ice-form becomes translucent. The warm light inside her is visible — a heartbeat of amber light.',
            whisperPool: [
                'Almost. Be gentle. They are almost done.',
                'She is warm now. She had forgotten what that was like.',
                'The fire is enough. It was always enough.',
            ],
        },
    },
};
// §3.4 — Embernorn Juggernaut (Ember Mire)
const EMBERNORN_JUGGERNAUT_CONTENT = {
    guardianType: 'emberborn_juggernaut',
    name: 'Embernorn Juggernaut',
    realmKey: 'ember_mire',
    baseHp: 4_500_000,
    durationTargetDays: 21,
    finalStillnessThreshold: 10,
    unburdeningRitualDurationMinutes: 5,
    unburdeningRitualDurationSoloMinutes: 10,
    unburdeningText: 'The forge remembered how to be warm without burning. The smith set down the hammer and did not pick it up again.',
    relicKey: 'juggernauts_hearthstone',
    contributions: [
        {
            key: 'forge_tending',
            name: 'Forge Tending',
            description: 'Feed materials to the forge. Each feeding contributes ~50 HP by reminding the forge how to breathe.',
            hpEquivalentPerUse: 50,
            ritualCategory: 'labor',
            durationMinutes: 2,
            materials: { wood: 1 }, // any fuel: wood, coal, ember-moss
        },
        {
            key: 'forge_bread',
            name: 'Forge Bread',
            description: 'Place forge-bread on the anvil during a resonance. Contributes ~200 HP and triggers a 30-second Tired state.',
            hpEquivalentPerUse: 200,
            ritualCategory: 'labor',
            durationMinutes: 5,
            materials: { flour: 1, ember_moss: 1, salt: 1 },
        },
        {
            key: 'chimney_reinforcement',
            name: 'Chimney Reinforcement',
            description: 'Reinforce the forge chimney. Increases all non-combat HP contributions by 20% for 8 real hours.',
            hpEquivalentPerUse: 0,
            ritualCategory: 'labor',
            durationMinutes: 15,
            materials: { stone: 3, iron: 1 },
        },
    ],
    phases: {
        [GuardianTonalPhase.Stillness]: {
            phase: GuardianTonalPhase.Stillness,
            behavior: {
                swingWindupMultiplier: 1.0,
                skipsSwings: false,
                movementStyle: 'rooted',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'steady_dim', // cold forge, no heat
                petBehavior: 'Cautious — stays behind player, watches the hammer.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Cold forge. Grey metal. No heat. Ash on everything. The room smells like rain on old coals.',
            resonanceVision: 'The forge sparks. One ember, briefly orange. The Juggernaut pauses, stares at it, then returns to hammering nothing.',
            whisperPool: [
                'We arrived. The forge is cold. They hammer at nothing.',
                'The sound of the hammer is metronomic. It does not change.',
                'Ash. Everywhere. The memory of heat without the heat.',
            ],
        },
        [GuardianTonalPhase.Weariness]: {
            phase: GuardianTonalPhase.Weariness,
            behavior: {
                swingWindupMultiplier: 1.15,
                skipsSwings: false,
                movementStyle: 'rooted',
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'flicker_on_drift',
                petBehavior: 'Curious — edges closer to the anvil between hammer-strokes.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 75,
            },
            environmentalShift: 'Warmth at edges. The metal in the walls begins to glow faintly — dull red, not hot.',
            resonanceVision: 'The forge lights. Real fire. The Juggernaut forges something — a shape emerges from the anvil. A tool? A gift? Before it is finished, the fire dies. The shape cools and crumbles.',
            whisperPool: [
                'The days run together. That is how you know it is working.',
                'They looked at what they were forging. Nothing was there.',
                'The forge lit. Just for a moment. Then nothing.',
            ],
        },
        [GuardianTonalPhase.Remembrance]: {
            phase: GuardianTonalPhase.Remembrance,
            behavior: {
                swingWindupMultiplier: 1.25,
                skipsSwings: true,
                movementStyle: 'rooted', // stands at anvil, hands flat
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'bright_during_resonance',
                petBehavior: 'Attentive — sits near the forge, watching the glow.',
                foodBuffMultiplier: 1.1,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 2,
                durationSeconds: 75,
            },
            environmentalShift: 'Warm. The forge-glow is steady. Ash is thinning — dark soil visible beneath. Small heat-loving insects appear.',
            resonanceVision: 'The Juggernaut sings. Not words — a low, rhythmic tone synced to hammer-strokes that are not happening. The forge responds: fire rises, metal hums. The room is a workshop. The Juggernaut is a smith. They were always a smith.',
            whisperPool: [
                'They are beginning to remember. I think we are helping.',
                'They sang. The forge answered.',
                'A smith. Not a monster. A smith who forgot to stop.',
            ],
        },
        [GuardianTonalPhase.Rest]: {
            phase: GuardianTonalPhase.Rest,
            behavior: {
                swingWindupMultiplier: 1.4,
                skipsSwings: true,
                movementStyle: 'stationary', // sets hammer down, sits
                alwaysVisible: true,
            },
            campMood: {
                fireState: 'low_ember_warm',
                petBehavior: 'Asleep near the anvil. The warmth is safe now.',
                foodBuffMultiplier: 1.0,
            },
            resonance: {
                baseIntervalMinutes: 30,
                eventsPerInterval: 1,
                durationSeconds: 90,
            },
            environmentalShift: 'Warm floor. Flowers grow through ash. The forge is cold but the room is warm.',
            resonanceVision: 'The anvil glows with the shape of the thing the Juggernaut was trying to make. It is a key. Or a bell. Or a cradle. The shape changes each time a player looks.',
            whisperPool: [
                'Almost. Be gentle. They are almost done.',
                'They set the hammer down. They did not pick it up.',
                'The forge is warm without burning. That is all it needed to remember.',
            ],
        },
    },
};
// ── Exports ──
/** All 4 Guardian encounter content records, indexed by guardianType. */
export const GUARDIAN_ENCOUNTER_CONTENT = {
    hollow_warden: HOLLOW_WARDEN_CONTENT,
    duneshade: DUNESHADE_CONTENT,
    frostbound_matriarch: FROSTBOUND_MATRIARCH_CONTENT,
    emberborn_juggernaut: EMBERNORN_JUGGERNAUT_CONTENT,
};
/** Ordered array for iteration. */
export const GUARDIAN_ENCOUNTER_LIST = [
    HOLLOW_WARDEN_CONTENT,
    DUNESHADE_CONTENT,
    FROSTBOUND_MATRIARCH_CONTENT,
    EMBERNORN_JUGGERNAUT_CONTENT,
];
/**
 * Look up the tonal phase for a given HP percentage.
 * Returns the matching phase or Rest if at 0%.
 */
export function getGuardianTonalPhase(hpPercent) {
    if (hpPercent > 75)
        return GuardianTonalPhase.Stillness;
    if (hpPercent > 50)
        return GuardianTonalPhase.Weariness;
    if (hpPercent > 25)
        return GuardianTonalPhase.Remembrance;
    return GuardianTonalPhase.Rest;
}
/**
 * Get the phase content for a Guardian at a given HP%.
 * Returns null if the guardianType is unknown.
 */
export function getGuardianPhaseContent(guardianType, hpPercent) {
    const guardian = GUARDIAN_ENCOUNTER_CONTENT[guardianType];
    if (!guardian)
        return null;
    const phase = getGuardianTonalPhase(hpPercent);
    return guardian.phases[phase];
}
//# sourceMappingURL=guardianEncounterContent.js.map